import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import DrawerHeaderStyled from './DrawerHeaderStyled';
import Logo from 'components/logo';
import { useLocation } from 'react-router-dom';

const FAMILY_LABELS = {
  in: 'IN', au: 'AU', ft: 'FT', pr: 'PR', ss: 'SS', ae: 'AE', cl: 'CL', mf: 'MF', mc: 'MC', mi: 'MI', rm: 'RM', ve: 'VE', ia: 'IA', vl: 'VL'
};

function normalizePathname(pathname) {
  if (!pathname) return '';
  const baseName = import.meta.env?.VITE_APP_BASE_NAME || '';
  let normalized = pathname;
  if (baseName && normalized.startsWith(baseName)) normalized = normalized.slice(baseName.length) || '/';
  if (!normalized.startsWith('/')) normalized = `/${normalized}`;
  return normalized;
}

function resolveAreaFromPath(pathname) {
  if (!pathname) return '';
  if (pathname.startsWith('/operarios')) return 'OPERARIOS';
  if (pathname.startsWith('/familias/')) {
    const [, , slug] = pathname.split('/');
    if (slug) return FAMILY_LABELS[slug] || slug.toUpperCase();
    return 'FAMILIAS';
  }
  if (pathname.startsWith('/cuadros')) return 'GESTION';
  if (pathname.startsWith('/i')) return 'IBERDROLA';
  if (pathname.startsWith('/v')) return 'VRP';
  if (pathname.startsWith('/contabilidad')) return 'CONTABILIDAD';
  if (pathname.startsWith('/produccion')) return 'PRODUCCIÓN';
  if (pathname.startsWith('/areas/informes')) return 'INFORMES';
  if (pathname.startsWith('/areas/mantenimientos-periodicos')) return 'REVISIONES';
  if (pathname.startsWith('/areas/instrumentacion')) return 'INSTRUMENTACIÓN';
  if (pathname.startsWith('/areas/reportes')) return 'REPORTES';
  if (pathname.startsWith('/areas/iso')) return 'CALIDAD';
  if (pathname.startsWith('/areas/presupuestos')) return 'PRESUPUESTOS';
  if (pathname.startsWith('/areas/plan-estrategico')) return 'PLAN ESTRATÉGICO';
  return '';
}

function resolvePlanEstrategicoTitle(pathname) {
  if (!pathname.startsWith('/areas/plan-estrategico')) return '';
  if (pathname.startsWith('/areas/plan-estrategico/documentacion-plan-estrategico')) return 'DOCUMENTACION';
  if (pathname.startsWith('/areas/plan-estrategico/imagen-corporativa')) return 'IMAGEN';
  if (pathname.startsWith('/areas/plan-estrategico/ciberseguridad')) return 'CIBERSEGURIDAD';
  if (pathname.startsWith('/areas/plan-estrategico/sostenibilidad')) return 'SOSTENIBILIDAD';
  if (pathname.startsWith('/areas/plan-estrategico/gestion-residuos')) return 'RESIDUOS';
  return 'PLAN ESTRATÉGICO';
}

export default function DrawerHeader({ open }) {
  const location = useLocation();
  const pathname = normalizePathname(location?.pathname || '');
  const fallbackPathname = typeof window !== 'undefined' ? normalizePathname(window.location?.pathname || '') : '';
  const areaLabel = (() => {
    try {
      const resolvedFromPath = resolvePlanEstrategicoTitle(pathname) || resolvePlanEstrategicoTitle(fallbackPathname);
      if (resolvedFromPath) return resolvedFromPath;

      const a = localStorage.getItem('current_area');
      if (a === 'vrp-cuadros') return 'CUADROS';
      if (a === 'operarios' && !pathname.startsWith('/areas')) return 'OPERARIOS';
      if (a === 'contabilidad') return 'CONTABILIDAD';
      if (a === 'iso') return 'CALIDAD';
      if (a === 'reportes') return 'REPORTES';
      if (a === 'produccion') return 'PRODUCCIÓN';
      if (a === 'presupuestos') return 'PRESUPUESTOS';
      if (a === 'plan-estrategico') return 'PLAN ESTRATÉGICO';
      if (a === 'mantenimientos-periodicos') return 'REVISIONES';
      if (a === 'instrumentacion') return 'INSTRUMENTACIÓN';
      if (a && a.startsWith('familia-')) {
        const slug = a.slice('familia-'.length).toLowerCase();
        return FAMILY_LABELS[slug] || slug.toUpperCase() || 'FAMILIAS';
      }
      if (a === 'v') return 'VRP';
      if (a === 'i') return 'IBERDROLA';
    } catch {}
    const resolvedFromPath = resolveAreaFromPath(pathname) || resolveAreaFromPath(fallbackPathname);
    if (resolvedFromPath) return resolvedFromPath;
    return 'VRP';
  })();

  const baseName = (import.meta.env?.VITE_APP_BASE_NAME || '').replace(/\/+$/, '');
  const areasHref = `${baseName || ''}/areas`;
  const publicHost = typeof window !== 'undefined' && window.location?.hostname ? window.location.hostname : 'Desconocida';

  return (
    <DrawerHeaderStyled
      open={open}
      sx={{
        minHeight: '60px',
        width: '100%',
        paddingTop: '8px',
        paddingBottom: '8px',
        paddingLeft: open ? '24px' : 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: open ? 'flex-start' : 'center'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Logo isIcon={!open} href={areasHref} sx={{ width: open ? 'auto' : 35, height: 35, ...(open ? {} : { mx: 'auto' }) }} />
        {open && (
          <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1.25, gap: 0.25 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                letterSpacing: 2,
                textTransform: 'uppercase',
                lineHeight: 1,
                textAlign: 'left',
                alignSelf: 'flex-start'
              }}
            >
              {areaLabel}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.125 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                CRM VRP 4.1
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {publicHost ? `IP ${publicHost}` : 'IP Desconocida'}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </DrawerHeaderStyled>
  );
}

DrawerHeader.propTypes = { open: PropTypes.bool };
